import { apiWrapper } from './apiWrapper';
import type {
  FinancialTransaction,
  FinancialTransactionWithChildren,
  CreateTransactionRequest,
  ApproveTransactionRequest,
} from '../../models/FinancialTransaction';

const BASE_URL = '/financial-transactions';

export interface PaginatedTransactionsResponse {
  data: FinancialTransaction[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const financialTransactionApi = {
  /**
   * Get all financial transactions with pagination support
   */
  async getTransactions(params?: {
    status?: string;
    transaction_type?: string;
    account_id?: number;
    date_from?: string;
    date_to?: string;
    provider?: string;
    parents_only?: boolean;
    association_id?: string;
    management_company_id?: string;
    discrepancy_filter?: 'all' | 'issues' | 'mismatches' | 'unmatched' | 'matched';
    page?: number;
    limit?: number;
  }): Promise<PaginatedTransactionsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.transaction_type) queryParams.append('transaction_type', params.transaction_type);
    if (params?.account_id) queryParams.append('account_id', params.account_id.toString());
    if (params?.date_from) queryParams.append('date_from', params.date_from);
    if (params?.date_to) queryParams.append('date_to', params.date_to);
    if (params?.provider) queryParams.append('provider', params.provider);
    if (params?.parents_only) queryParams.append('parents_only', 'true');
    if (params?.association_id) queryParams.append('association_id', params.association_id);
    if (params?.management_company_id) queryParams.append('management_company_id', params.management_company_id);
    if (params?.discrepancy_filter && params.discrepancy_filter !== 'all') queryParams.append('discrepancy_filter', params.discrepancy_filter);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const url = `${BASE_URL}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiWrapper(url);
  },

  /**
   * Get a specific transaction by ID
   */
  async getTransaction(
    transactionId: number,
    includeChildren = true,
    includeRelated = true
  ): Promise<FinancialTransactionWithChildren> {
    const params = new URLSearchParams();
    if (includeChildren) params.append('include_children', 'true');
    if (includeRelated) params.append('include_related', 'true');
    const url = `${BASE_URL}/${transactionId}${params.toString() ? `?${params.toString()}` : ''}`;
    return apiWrapper(url);
  },

  /**
   * Create a new transaction
   */
  async createTransaction(data: CreateTransactionRequest): Promise<FinancialTransaction> {
    return apiWrapper(BASE_URL, {
      method: 'POST',
      body: data, // Don't stringify - apiWrapper will do it
    });
  },

  /**
   * Approve a transaction
   */
  async approveTransaction(
    transactionId: number,
    data: ApproveTransactionRequest
  ): Promise<FinancialTransaction> {
    return apiWrapper(`${BASE_URL}/${transactionId}/approve`, {
      method: 'POST',
      body: data, // Don't stringify - apiWrapper will do it
    });
  },

  /**
   * Cancel a transaction
   */
  async cancelTransaction(transactionId: number): Promise<FinancialTransaction> {
    return apiWrapper(`${BASE_URL}/${transactionId}/cancel`, {
      method: 'POST',
    });
  },

  /**
   * Verify a transaction
   */
  async verifyTransaction(transactionId: number): Promise<FinancialTransaction> {
    return apiWrapper(`${BASE_URL}/${transactionId}/verify`, {
      method: 'POST',
    });
  },

  /**
   * Execute a transaction
   */
  async executeTransaction(transactionId: number): Promise<FinancialTransaction> {
    return apiWrapper(`${BASE_URL}/${transactionId}/execute`, {
      method: 'POST',
    });
  },

  /**
   * Get reconciliation summary statistics
   */
  async getReconciliationStats(): Promise<{
    total_amount: number;
    total_expected: number;
    variance: number;
    total_count: number;
    discrepancy_count: number;
    unmatched_count: number;
    matched_count: number;
    order_payment_count: number;
  }> {
    return apiWrapper(`${BASE_URL}/reconciliation-stats`);
  },
};
